import { PerspectiveCamera } from '@react-three/drei'
import { Canvas } from '@react-three/fiber'
import {
  ExtraLabelDisplayFn,
  MapBackgroundColor,
  UnitDisplayPropertiesFn,
} from 'features/siteMapViz'
import { useSiteViewContext } from 'features/siteView'
import { EnvironmentControlsR3f } from 'gecko-3d-tiles-renderer'
import { SiteModelProvider } from 'pages/siteView/siteMap/contexts/SiteModelContext'
import {
  MapInteractionAPIs,
  SiteViewMapProps,
} from 'pages/siteView/siteMap/SiteViewMap'
import { TilesetMapRaycastIndicator } from 'pages/siteView/siteMap/tilesetMap/objects/TilesetMapRaycastIndicator'
import { TilesetMapFeatures } from 'pages/siteView/siteMap/tilesetMap/TilesetMapFeatures'
import React, { Dispatch, RefObject, SetStateAction, useCallback } from 'react'
import styled from 'styled-components'
import { Vector3 } from 'three'
import * as THREE from 'three'
import { GeckoGeographicPin, GeckoSiteModel, GeckoUnit } from 'types'

import { CameraFlightHandler } from './features/CameraFlightHandler'
import { MapVizInternalProvider } from './MapVizInternalProvider'
import { HtmlOverlayEntry } from './objects/HTMLOverlay'
import { OverlaysHandler } from './OverlaysHandler'
import { TilesRendererWrapper } from './TilesRendererWrapper'

const Container = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`

const HtmlOverlaysContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  pointer-events: none;
`
export const MaxarTilesetMapViz: React.FC<{
  allUnitsAtSite: ReadonlyArray<GeckoUnit>
  siteModel: GeckoSiteModel
  setSiteModel: (siteModel: GeckoSiteModel) => void
  isInEditMode: boolean
  setIsInEditMode: Dispatch<SetStateAction<boolean>>

  /**
   * Function that will return the color and pattern for a given unit
   * Note: This function needs to be appropriately memoized
   * Note: Any alpha-value included in the color will be ignored
   */
  getUnitDisplayProperties: UnitDisplayPropertiesFn

  /**
   * Function that will return an extra react-node to be displayed in the label
   * for an asset
   *
   * Note: This function needs to be appropriately memoized
   * Note: The React-Node returned cannot use contexts -- React-Three-Fiber
   *        appears to be rendering this outside of our react-tree so it will
   *        fail if you do
   *        TODO [ARCH-330]: Make labels work with contexts
   */
  getExtraLabelDisplay: ExtraLabelDisplayFn

  /**
   *  A function that will be called on units that have tagged site-model entries, to determine if they should be displayed
   *  A return of undefined means the tagged unit was not found
   */
  shouldDisplayTaggedUnit: NonNullable<
    MapInteractionAPIs['shouldDisplayTaggedUnit']
  >
  unitIDFlyingTo: string | undefined
  setUnitIDFlyingTo: (unitID: string | undefined) => void
  addMapFeatureMenuLocationRef?: RefObject<HTMLDivElement>
  pins: readonly GeckoGeographicPin[]
  /**
   * The (functional) component to render when a feature is clicked, which will be displayed above the selected feature.
   * This is specified using the functional paradigm so you can specify other on-click actions like navigate.
   *
   * NOTE: The returned ReactNode will not have a specified parent width/height, so 100% width/height won't work
   */
  componentToDisplayOnFeatureClick: SiteViewMapProps['componentToDisplayOnFeatureClick']
}> = ({ siteModel, ...props }) => {
  const { raycastEnabled, siteViewRaycastConfig, setCamera } =
    useSiteViewContext()

  const cameraRef = React.useRef<THREE.PerspectiveCamera>(null)

  React.useEffect(() => {
    setCamera(cameraRef)
  }, [setCamera])

  // HtmlOverlays must be handled in a custom way, using a combination of refs
  // for easy access and an actual array for iteration
  const htmlOverlaysRef = React.useRef(new Array<HtmlOverlayEntry>())
  const [htmlOverlays, setHtmlOverlays] = React.useState<HtmlOverlayEntry[]>([])
  React.useEffect(() => {
    htmlOverlaysRef.current = htmlOverlays
  }, [htmlOverlays])
  const viewportSizeDuringAnimation = React.useRef<Vector3>(
    new Vector3(0, 0, 0)
  )
  const [showUntaggedModels, setShowUntaggedModels] = React.useState(true)

  const getUnitDisplayName = useCallback(
    (unitID: string) => {
      return props.allUnitsAtSite.find(({ id }) => id === unitID)?.data.name
    },
    [props.allUnitsAtSite]
  )

  return (
    <Container>
      <SiteModelProvider
        siteModel={siteModel}
        setSiteModel={props.setSiteModel}>
        <OverlaysHandler
          siteModel={siteModel}
          addMapFeatureMenuLocationRef={props.addMapFeatureMenuLocationRef}
          showUntaggedModels={showUntaggedModels}
          setShowUntaggedModels={setShowUntaggedModels}
          isInEditMode={props.isInEditMode}
          setIsInEditMode={props.setIsInEditMode}
          componentToDisplayOnFeatureClick={
            props.componentToDisplayOnFeatureClick
          }
          allUnitsAtSite={props.allUnitsAtSite}>
          <MapVizInternalProvider
            value={{
              viewportSizeDuringAnimation,
              htmlOverlaysRef,
              setHtmlOverlays,
            }}>
            <Canvas
              id={'site-view-map-canvas'}
              style={{
                position: 'relative',
                height: '100%',
                width: '100%',
                backgroundColor: MapBackgroundColor,
              }}>
              <CameraFlightHandler
                unitIDFlyingTo={props.unitIDFlyingTo}
                setUnitIDFlyingTo={props.setUnitIDFlyingTo}
              />
              <TilesRendererWrapper />
              <PerspectiveCamera
                ref={cameraRef}
                fov={30}
                far={100000}
                makeDefault={true}
                position={siteModel.data.camera_origin.position}
                rotation={siteModel.data.camera_origin.rotation}
              />
              <TilesetMapFeatures
                shouldDisplayTaggedUnit={props.shouldDisplayTaggedUnit}
                getUnitDisplayName={getUnitDisplayName}
                showUntaggedModels={showUntaggedModels}
                getUnitDisplayProperties={props.getUnitDisplayProperties}
                getExtraLabelDisplay={props.getExtraLabelDisplay}
                pins={props.pins}
              />
              <ambientLight intensity={1.4} />
              <directionalLight position={[10, 10, 5]} intensity={1.5} />
              <EnvironmentControlsR3f
                enableDamping={true}
                maxDistance={50000}
              />
              {raycastEnabled && siteViewRaycastConfig && (
                <TilesetMapRaycastIndicator
                  config={siteViewRaycastConfig}
                  getUnitDisplayProperties={props.getUnitDisplayProperties}
                  getUnitDisplayName={getUnitDisplayName}
                />
              )}
            </Canvas>
          </MapVizInternalProvider>
          <HtmlOverlaysContainer>
            {htmlOverlays.map(({ id, node }) => (
              <div key={id}>{node}</div>
            ))}
          </HtmlOverlaysContainer>
        </OverlaysHandler>
      </SiteModelProvider>
    </Container>
  )
}
